import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Backdrop,
  CircularProgress,
} from '@mui/material';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
// mock
import { useNavigate } from 'react-router-dom';
import { baseUrl, postUrl } from 'src/api';
import axios from 'axios';
import Swal from 'sweetalert2';
import { notifyError, notifySuccess } from 'src/utils/Toast';
import Context from 'src/context/context';
import { ToastContainer } from 'react-toastify';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'title', label: 'Title', alignRight: false },
  { id: 'category', label: 'Category', alignRight: false },
  { id: 'view', label: 'Views', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: "edit", label: "Edit", align: false },
  { id: "delete", label: "Delete", align: false },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  if (!array || array.length === 0) return [];
  
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  
  if (query) {
    return filter(array, (_user) => _user.title.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function PostPage() {
  const { token } = useContext(Context);
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [loader, setLoader] = useState(false);
  const [posts, setPosts] = useState([]);
  const [lastPage, setLastPage] = useState(null);
  const [totalLength, setTotalLength] = useState(0);

  // Fetch posts for specific page
  const fetchPosts = useCallback((pageNumber = 1) => {
    setLoader(true);
    
    let config = {
      method: 'get',
      url: `${postUrl}?page=${pageNumber}&per_page=${rowsPerPage}`,
      headers: { 
        'Authorization': `Bearer ${token}`
      }
    };
    
    axios.request(config)
      .then((response) => {
        setPosts(response?.data?.posts?.data || []);
        setLastPage(response?.data?.posts?.last_page);
        setTotalLength(response?.data?.posts?.total);
        setLoader(false);
      })
      .catch((error) => {
        console.error('Error fetching posts:', error);
        notifyError('Failed to load posts');
        setLoader(false);
        setPosts([]);
      });
  }, [token, rowsPerPage]);

  // Initial load
  useEffect(() => {
    fetchPosts(1);
  }, [fetchPosts]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchPosts(1);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  // **REMOVED THE SLICING - Let the API handle pagination**
  const filteredUsers = applySortFilter(posts, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers?.length && !!filterName;

  // handle delete category
  const handleDeleteRow = (id) => {
    const url = `${postUrl}/${id}`;
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(url, { headers: { Authorization: `Bearer ${token}` } })
          .then((response) => {
            notifySuccess(response?.data?.message);
            fetchPosts(page + 1); // Reload current page
          })
          .catch((e) => notifyError('Failed to delete post'));
      }
    });
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    const pageNumber = newPage + 1; // MUI pagination is 0-indexed, API is 1-indexed
    setPage(newPage);
    fetchPosts(pageNumber);
  };

  console.log('posts:', posts);
  console.log('filteredUsers:', filteredUsers);

  return (
    <>
      <Helmet>
        <title> Posts | List </title>
      </Helmet>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Posts
          </Typography>
          <Button onClick={() => navigate('/dashboard/posts/store')} variant="contained" startIcon={<Iconify icon="eva:plus-fill" />}>
            New Post
          </Button>
        </Stack>

        <Card>
          <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={posts?.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {/* REMOVED SLICE - Show all posts from current page */}
                  {filteredUsers?.map((row) => {
                    const { id, title, image, views, status, categorys } = row;
                    const selectedUser = selected.indexOf(title) !== -1;
                    const firstTenWords = title.split(" ").slice(0, 7).join(" ");

                    return (
                      <TableRow hover key={id} tabIndex={-1} role="checkbox" selected={selectedUser}>
                        <TableCell></TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar alt={title} src={`${baseUrl}/storage/${image}`} />
                            <Typography variant="subtitle2">
                              {firstTenWords}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left" style={{ textTransform: 'uppercase' }}>
                          {categorys?.category_name}
                        </TableCell>
                        <TableCell align="left" style={{ textTransform: 'uppercase' }}>
                          {views}
                        </TableCell>

                        <TableCell align="left">
                          <Label color={(status === 'banned' && 'error') || 'success'}>active</Label>
                        </TableCell>

                        <TableCell align="left">
                          <Button variant='outlined' onClick={() => navigate('/dashboard/posts/store', { state: { row: row } })}>
                            Edit
                          </Button>
                        </TableCell>
                        <TableCell variant='contained' align="left">
                          <Button onClick={() => handleDeleteRow(id)} style={{ color: '#FF0000' }}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>
                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[100]}
            component="div"
            count={totalLength}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <ToastContainer />
    </>
  );
}